import React from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import {
  addCommasToNumbersAndRound,
  percentageToNdecialPlaces,
} from '../../../../../utilities/numberFormatters';
import { mainColors } from '../../../../../styling/theme';

interface SubFundPerformanceTableProps {
  data: any;
}

interface SubFundPerformanceTableData {
  asset: string;
  assetClass: string;
  assetType: string;
  positionSize: number;
  positionSizeStart: number | string;
  positionSizeEnd: number | string;
  baseCurrency: string;
  entryDate: string;
  exitDate: string;
  currencyPL: number;
  currencyPctPL: number;
  assetPL: number;
  assetPctPL: number;
  totalPL: number;
  totalPctPL: number;
  isAggregateRow: boolean;
}

addCommasToNumbersAndRound;

const detailColumns: CustomColumn<SubFundPerformanceTableData>[] = [
  {
    title: 'Asset',
    field: 'asset',
    width: '12%',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Asset Class',
    field: 'assetClass',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Asset Type',
    field: 'assetType',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Position Size Start',
    field: 'positionSizeStart',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      addCommasToNumbersAndRound(rowData.positionSizeStart),
  },
  {
    title: 'Position Size End',
    field: 'positionSizeEnd',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      addCommasToNumbersAndRound(rowData.positionSizeEnd),
  },
  {
    title: 'Base Currency',
    field: 'baseCurrency',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'In-Scope Entry Date',
    field: 'entryDate',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'In-Scope Exit Date',
    field: 'exitDate',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Asset PL',
    field: 'assetPL',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      addCommasToNumbersAndRound(rowData.assetPL),
  },
  {
    title: 'Asset PL %',
    field: 'assetPctPL',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      percentageToNdecialPlaces(rowData.assetPctPL / 100, 2),
  },
  {
    title: 'Currency PL',
    field: 'currencyPL',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      addCommasToNumbersAndRound(rowData.currencyPL),
  },
  {
    title: 'Currency PL %',
    field: 'currencyPctPL',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      percentageToNdecialPlaces(rowData.currencyPctPL / 100, 2),
  },
  {
    title: 'Total PL',
    field: 'totalPL',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      addCommasToNumbersAndRound(rowData.totalPL),
  },
  {
    title: 'Total PL %',
    field: 'totalPctPL',
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
    render: (rowData: SubFundPerformanceTableData) =>
      percentageToNdecialPlaces(rowData.totalPctPL / 100, 2),
  },
];

const buildTableData = (inputData: any): SubFundPerformanceTableData[] => {
  if (!inputData.data || !inputData.data.length) {
    return [];
  } else {
    const uniqueAssetClasses = [
      ...new Set(
        Object.keys(inputData.data[0].position_level).map(
          (asset: any) => inputData.data[0].position_level[asset].asset_class,
        ),
      ),
    ];
    let returnData: SubFundPerformanceTableData[] = [];
    uniqueAssetClasses.map((category: string) => {
      const categoryData: SubFundPerformanceTableData[] = [];
      let aggregatePositionSize = 0;
      let aggregateCurrencyPL = 0;
      let aggregateCurrencyPLPct = 0;
      let aggregateAssetPL = 0;
      let aggregateAssetPLPct = 0;
      let aggregateTotalPL = 0;
      let aggregateTotalPLPct = 0;
      Object.keys(inputData.data[0].position_level).map((asset) => {
        if (inputData.data[0].position_level[asset].asset_class === category) {
          aggregatePositionSize +=
            inputData.data[0].position_level[asset].mr_position;
          aggregateCurrencyPL +=
            inputData.data[0].position_level[asset].aggregate_currency_pl;
          aggregateCurrencyPLPct +=
            inputData.data[0].position_level[asset].aggregate_currency_pl_pct;
          aggregateAssetPL +=
            inputData.data[0].position_level[asset].aggregate_asset_pl;
          aggregateAssetPLPct +=
            inputData.data[0].position_level[asset].aggregate_asset_pl_pct;
          aggregateTotalPL +=
            inputData.data[0].position_level[asset].aggregate_pl;
          aggregateTotalPLPct +=
            inputData.data[0].position_level[asset].aggregate_pct_pl;
          categoryData.push({
            asset: inputData.data[0].position_level[asset].asset_name,
            assetClass: inputData.data[0].position_level[asset].asset_class,
            assetType: inputData.data[0].position_level[asset].asset_type,
            positionSize: inputData.data[0].position_level[asset].mr_position,
            positionSizeStart:
              inputData.data[0].position_level[asset].mr_position_start,
            positionSizeEnd:
              inputData.data[0].position_level[asset].mr_position_end,
            baseCurrency: inputData.data[0].position_level[asset].base_currency,
            entryDate: inputData.data[0].position_level[asset].entry_date,
            exitDate: inputData.data[0].position_level[asset].end_date,
            currencyPL:
              inputData.data[0].position_level[asset].aggregate_currency_pl,
            currencyPctPL:
              inputData.data[0].position_level[asset].aggregate_currency_pl_pct,
            assetPL: inputData.data[0].position_level[asset].aggregate_asset_pl,
            assetPctPL:
              inputData.data[0].position_level[asset].aggregate_asset_pl_pct,
            totalPL: inputData.data[0].position_level[asset].aggregate_pl,
            totalPctPL:
              inputData.data[0].position_level[asset].aggregate_pct_pl,
            isAggregateRow: false,
          });
        }
      });
      categoryData.unshift({
        asset: `${category} (Aggregation)`,
        assetClass: category,
        assetType: 'Aggregate',
        positionSize: aggregatePositionSize,
        positionSizeStart: '',
        positionSizeEnd: '',
        baseCurrency: '',
        entryDate: '',
        exitDate: '',
        currencyPL: aggregateCurrencyPL,
        currencyPctPL: aggregateCurrencyPLPct,
        assetPL: aggregateAssetPL,
        assetPctPL: aggregateAssetPLPct,
        totalPL: aggregateTotalPL,
        totalPctPL: aggregateTotalPLPct,
        isAggregateRow: true,
      });
      returnData = returnData.concat(categoryData);
    });
    return returnData;
  }
};

const SubFundPerformanceTable: React.FC<SubFundPerformanceTableProps> = (
  props,
) => {
  const builtTableData = buildTableData(props.data);

  return (
    <GridItem xs={12} card>
      <CustomTable<SubFundPerformanceTableData>
        columns={detailColumns}
        showToolbar
        data={builtTableData}
        title={'Returns By Asset'}
        options={{
          paging: false,
          search: true,
          exportButton: true,
          rowStyle: (rowData: SubFundPerformanceTableData) => ({
            backgroundColor: rowData.isAggregateRow
              ? mainColors.lightGrey
              : 'white',
          }),
        }}
      />
    </GridItem>
  );
};

export default SubFundPerformanceTable;
